import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ensureMe } from '@/features/auth/useMe.ts';
import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Plane, Route as RouteIcon, Compass } from 'lucide-react';
import { useT } from '@mosaferati/i18n';
import { Button, Icon, springs } from '@mosaferati/ui';
import { usePrefs } from '@/app/prefs-store.ts';

export const Route = createFileRoute('/onboarding')({
  beforeLoad: async () => {
    if (await ensureMe()) throw redirect({ to: '/', replace: true });
  },
  component: Onboarding,
});

const SLIDES = [
  { icon: Plane, title: 'onboarding.slide1.title', body: 'onboarding.slide1.body' },
  { icon: RouteIcon, title: 'onboarding.slide2.title', body: 'onboarding.slide2.body' },
  { icon: Compass, title: 'onboarding.slide3.title', body: 'onboarding.slide3.body' },
] as const;

function Onboarding() {
  const t = useT();
  const navigate = useNavigate();
  const setOnboarded = usePrefs((s) => s.setOnboarded);
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;
  const finish = (to: '/' | '/auth/phone') => {
    setOnboarded(true);
    void navigate({ to, replace: true });
  };
  const s = SLIDES[i]!;
  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-bg-canvas">
      {/* decorative route line */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-40" viewBox="0 0 400 800" preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--m-color-brand)" stopOpacity="0" />
            <stop offset="0.5" stopColor="var(--m-color-brand)" />
            <stop offset="1" stopColor="var(--m-color-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path d="M -20 620 C 120 520, 180 720, 300 480 S 460 260, 420 120" fill="none" stroke="url(#g)" strokeWidth="2.5" strokeDasharray="6 10" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.4, ease: 'easeInOut' }} />
      </svg>
      <div className="pt-safe flex justify-end px-4 pt-3">
        <button type="button" className="text-caption text-text-tertiary" onClick={() => finish('/')}>
          {t('onboarding.skip')}
        </button>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={springs.default} className="flex flex-col items-center gap-5">
            <div className="flex h-24 w-24 items-center justify-center text-text-primary">
              <Icon icon={s.icon} size={44} strokeWidth={1.5} />
            </div>
            <h1 className="text-h1 font-bold">{t(s.title)}</h1>
            <p className="max-w-[30ch] text-body-lg text-text-secondary">{t(s.body)}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="pb-safe flex flex-col gap-3 px-6 pb-6">
        <div className="mb-2 flex justify-center gap-1.5">
          {SLIDES.map((_, k) => (
            <motion.span key={k} animate={{ width: k === i ? 22 : 6 }} transition={springs.snappy} className={k === i ? 'h-1.5 rounded-pill bg-tint' : 'h-1.5 rounded-pill bg-border-strong'} />
          ))}
        </div>
        <Button size="lg" full onClick={() => (last ? finish('/auth/phone') : setI(i + 1))}>
          {last ? t('onboarding.start') : t('common.next')}
        </Button>
        <Button size="lg" full variant="ghost" onClick={() => finish('/auth/phone')}>
          {t('onboarding.login')}
        </Button>
      </div>
    </div>
  );
}
